import type { CSSProperties, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Chip, Icon } from '@/components/readiness/primitives'
import type { FieldMeta, MigrationItemStatus } from '@/api/types/migration'
import { ITEM_STATUS_LABEL, SOURCE_LABEL } from '@/api/types/migration'
import { STATUS_TOKEN } from '@/lib/migration/summary'
import { timeAgo } from '@/lib/readiness/util'

// ── Shared pieces for the submission planning screens ────────────────────────

// Provenance chip (§10.2): `Discovery`, `CBOM`, `Playbook`, `Vendors`,
// `Migration item`, `Readiness`, `Manual`; an edited auto-filled field shows
// `Manual · was {source}` with a Revert link.
export function SourceChip({ meta, onRevert, size = 'sm' }: { meta: FieldMeta; onRevert?: () => void; size?: 'sm' | 'xs' }) {
  const fs = size === 'xs' ? 10 : 11
  if (meta.overridden) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <Chip tone="neutral" style={{ fontSize: fs }} title={meta.source_changed ? `The ${SOURCE_LABEL[meta.was ?? meta.source]} value has changed since this override` : undefined}>
          Manual · was {SOURCE_LABEL[meta.was ?? meta.source]}
          {meta.source_changed && <Icon name="info" size={11} style={{ color: 'var(--rd-status-warn)' }} />}
        </Chip>
        {onRevert && <button type="button" className="rd-link text-[11.5px]" onClick={onRevert}>Revert</button>}
      </span>
    )
  }
  if (meta.source === 'manual') return <Chip tone="neutral" style={{ fontSize: fs }}>Manual</Chip>
  const tone = meta.source === 'discovery' || meta.source === 'cbom' ? 'ok' : meta.source === 'migration_item' ? 'info' : meta.source === 'readiness' ? 'chip' : 'tile'
  return (
    <Chip tone={tone} style={{ fontSize: fs, fontWeight: 500, letterSpacing: 0 }} title={meta.synced_at ? `${SOURCE_LABEL[meta.source]}${meta.source_ref ? ` · ${meta.source_ref}` : ''} · synced ${timeAgo(meta.synced_at)}` : undefined}>
      {SOURCE_LABEL[meta.source]}
    </Chip>
  )
}

// A labelled form field with its source chip, the optional verbatim CSA
// wording beneath the label, and an input or textarea.
export function PlanField({ id, label, csa, showCsa, meta, onRevert, value, onChange, onBlur, long, rows = 3, placeholder, full, children, readOnly, hint }: {
  id: string
  label: string
  csa?: string
  showCsa?: boolean
  meta: FieldMeta
  onRevert?: () => void
  value?: string
  onChange?: (v: string) => void
  // Called when the field loses focus: the forms flush their pending writes.
  onBlur?: () => void
  long?: boolean
  rows?: number
  placeholder?: string
  full?: boolean
  children?: ReactNode
  readOnly?: boolean
  hint?: ReactNode
}) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <label htmlFor={id} className="text-[12.5px] font-medium" style={{ color: 'var(--rd-text-secondary)' }}>{label}</label>
        <SourceChip meta={meta} onRevert={meta.overridden ? onRevert : undefined} />
      </div>
      {showCsa && csa && <p className="mb-1 text-[11.5px] italic leading-snug" style={{ color: 'var(--rd-text-faint)' }}>CSA: “{csa}”</p>}
      {children ?? (long ? (
        <textarea id={id} className="rd-input" rows={rows} value={value ?? ''} placeholder={placeholder} readOnly={readOnly} onChange={(e) => onChange?.(e.target.value)} onBlur={onBlur} />
      ) : (
        <input id={id} className="rd-input" value={value ?? ''} placeholder={placeholder} readOnly={readOnly} onChange={(e) => onChange?.(e.target.value)} onBlur={onBlur} />
      ))}
      {hint && <p className="mt-1 text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }}>{hint}</p>}
    </div>
  )
}

export function StatusChip({ status, style }: { status: MigrationItemStatus; style?: CSSProperties }) {
  const tone = status === 'in_progress' ? 'info' : status === 'verified' ? 'warn' : status === 'closed' ? 'ok' : 'neutral'
  return (
    <Chip tone={tone} style={style}>
      <span className="inline-block rounded-full" style={{ width: 7, height: 7, background: STATUS_TOKEN[status] }} aria-hidden="true" /> {ITEM_STATUS_LABEL[status]}
    </Chip>
  )
}

// Thin progress bar in a given colour.
export function Bar({ value, max, color = 'var(--rd-accent-primary)', height = 6, className = '' }: { value: number; max: number; color?: string; height?: number; className?: string }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (100 * value) / max)) : 0
  return (
    <div className={`w-full overflow-hidden rounded-full ${className}`} style={{ height, background: 'var(--rd-bg-tile)' }} role="img" aria-label={`${value} of ${max}`}>
      <div style={{ width: `${pct}%`, height, background: color, borderRadius: 999 }} />
    </div>
  )
}

export function KpiTile({ label, value, sub, warn, testId, children }: { label: string; value: ReactNode; sub?: ReactNode; warn?: ReactNode; testId?: string; children?: ReactNode }) {
  return (
    <section className="rd-card min-h-[120px] p-5" data-testid={testId}>
      <p className="rd-label">{label}</p>
      <p className="rd-kpi mt-2">{value}</p>
      {sub && <p className="mt-1.5 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>{sub}</p>}
      {warn && <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-status-warn)' }}>{warn}</p>}
      {children}
    </section>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="rd-card p-8 text-center text-[14px]" style={{ color: 'var(--rd-text-muted)' }}>{children}</div>
}

// Hub-wide notice when the index has no submission (QPI): the whole section is hidden.
export function HiddenForIndex({ indexId, name }: { indexId: string; name: string }) {
  return (
    <Empty>
      Submission planning is hidden for {name}: this index has no CSA submission. <Link to={`/readiness?index=${indexId}`} className="rd-link">Back to the overview →</Link>
    </Empty>
  )
}
